/**
 * Typography Engine — font pairings, modular scale, and hierarchy roles.
 *
 * Given a pairing id and a base size, resolveTypography() returns a spec per
 * role (display, headline, subheadline, body, caption, cta) with family,
 * weight, size, letter spacing, and line height. The composition engine maps
 * grammar nodes onto these roles; nothing here touches geometry or color.
 */

export const FONT_PAIRINGS = {
  'modern-sans': {
    id: 'modern-sans',
    name: 'Modern Sans',
    heading: 'Poppins',
    body: 'Inter',
    mood: 'clean, contemporary',
  },
  'elegant-serif': {
    id: 'elegant-serif',
    name: 'Elegant Serif',
    heading: 'Playfair Display',
    body: 'Source Sans Pro',
    mood: 'luxury, editorial',
  },
  'bold-display': {
    id: 'bold-display',
    name: 'Bold Display',
    heading: 'Montserrat',
    body: 'Open Sans',
    mood: 'loud, promotional',
  },
  'friendly-round': {
    id: 'friendly-round',
    name: 'Friendly Round',
    heading: 'Nunito',
    body: 'Nunito',
    mood: 'soft, approachable',
  },
};

export const listFontPairings = () =>
  Object.values(FONT_PAIRINGS).map(({ id, name, mood }) => ({ id, name, mood }));

/** Modular scale ratios by hierarchy intensity. */
export const SCALE_RATIOS = { subtle: 1.2, balanced: 1.333, dramatic: 1.5 };

/**
 * Hierarchy roles, ordered top-down. `step` is the modular-scale exponent
 * applied to the base size; spacing/height are typographic constants that
 * read well at flyer sizes.
 */
const ROLES = [
  { role: 'display', step: 4, weight: 800, letterSpacing: -1, lineHeight: 1.05, family: 'heading' },
  { role: 'headline', step: 3, weight: 700, letterSpacing: -0.5, lineHeight: 1.1, family: 'heading' },
  { role: 'subheadline', step: 2, weight: 600, letterSpacing: 0, lineHeight: 1.2, family: 'heading' },
  { role: 'cta', step: 1, weight: 700, letterSpacing: 0.5, lineHeight: 1, family: 'heading' },
  { role: 'body', step: 0, weight: 400, letterSpacing: 0, lineHeight: 1.5, family: 'body' },
  { role: 'caption', step: -1, weight: 400, letterSpacing: 0.3, lineHeight: 1.4, family: 'body' },
];

export const TYPE_ROLES = ROLES.map((r) => r.role);

/**
 * Resolve a full typography spec.
 * options = { pairingId, baseSize = 16, scale = 'balanced' }
 * Returns { pairing, scale, roles: { role: { fontFamily, fontSize, fontWeight,
 * letterSpacing, lineHeight, align } } }
 */
export const resolveTypography = ({ pairingId, baseSize = 16, scale = 'balanced' } = {}) => {
  const pairing = FONT_PAIRINGS[pairingId];
  if (!pairing) throw new Error(`Unknown font pairing "${pairingId}"`);
  const ratio = SCALE_RATIOS[scale];
  if (!ratio) throw new Error(`Unknown scale "${scale}" (use ${Object.keys(SCALE_RATIOS).join('/')})`);

  const roles = {};
  ROLES.forEach(({ role, step, weight, letterSpacing, lineHeight, family }) => {
    roles[role] = {
      fontFamily: family === 'heading' ? pairing.heading : pairing.body,
      fontSize: Math.round(baseSize * Math.pow(ratio, step)),
      fontWeight: weight,
      letterSpacing,
      lineHeight,
    };
  });

  return { pairingId: pairing.id, scale, ratio, roles };
};

/** Map grammar node types to typography roles. */
export const NODE_TYPE_ROLE = {
  primaryHeading: 'display',
  secondaryHeading: 'subheadline',
  body: 'body',
  offer: 'headline',
  cta: 'cta',
  footer: 'caption',
};
